import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, ListChecks, TrendingUp, Dumbbell, LogOut, ChevronLeft, Settings, X } from "lucide-react";
import { clearAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import WaypointLogo from "@/components/WaypointLogo";
import { useTheme, THEMES } from "@/lib/theme";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/routines", icon: ListChecks, label: "Routines" },
  { to: "/exercises", icon: Dumbbell, label: "Exercises" },
  { to: "/progress", icon: TrendingUp, label: "Progress" },
];

function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

function OnlineIndicator() {
  const online = useOnline();
  if (online) return null;
  return (
    <span className="rounded-full bg-yellow-900/50 px-2 py-0.5 text-xs text-yellow-400">
      offline
    </span>
  );
}

const ROOT_PATHS = new Set(["/", "/routines", "/exercises", "/progress"]);

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isRoot = ROOT_PATHS.has(location.pathname);
  const { theme, setTheme } = useTheme();
  const [settingsOpen, setSettingsOpen] = useState(false);

  function logout() {
    clearAuth();
    navigate("/login");
  }

  return (
    <div className="flex h-screen flex-col bg-zinc-950 text-zinc-50">
      <header className="fixed inset-x-0 top-0 z-30 flex h-14 items-center justify-between border-b border-zinc-800 bg-zinc-950 px-4">
        {isRoot ? (
          <div className="flex items-center gap-2 font-semibold">
            <WaypointLogo className="h-5 w-5 text-brand" />
            <span>Waypoint</span>
          </div>
        ) : (
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-sm text-zinc-400 hover:text-zinc-200 transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
            Back
          </button>
        )}
        <div className="flex items-center gap-3">
          <OnlineIndicator />
          <button
            onClick={() => setSettingsOpen(true)}
            className="text-zinc-500 hover:text-zinc-200 transition-colors"
          >
            <Settings className="h-4 w-4" />
          </button>
          <button
            onClick={logout}
            className="text-zinc-500 hover:text-zinc-200 transition-colors"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto pb-20 pt-14">
        <Outlet />
      </main>

      <nav className="fixed bottom-0 left-0 right-0 flex border-t border-zinc-800 bg-zinc-950">
        {NAV.map(({ to, icon: Icon, label, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              cn(
                "flex flex-1 flex-col items-center gap-1 py-2 text-xs transition-colors",
                isActive ? "text-brand" : "text-zinc-500 hover:text-zinc-300"
              )
            }
          >
            <Icon className="h-5 w-5" />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Settings panel */}
      {settingsOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/60"
            onClick={() => setSettingsOpen(false)}
          />
          <div className="fixed inset-x-0 bottom-0 z-50 rounded-t-xl border-t border-zinc-800 bg-zinc-900 p-5 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">Settings</h2>
              <button
                onClick={() => setSettingsOpen(false)}
                className="text-zinc-500 hover:text-zinc-200 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wider text-zinc-400">
                Theme
              </p>
              <div className="flex gap-2">
                {THEMES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTheme(t.id)}
                    className={cn(
                      "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors",
                      theme === t.id
                        ? "border-brand text-zinc-100"
                        : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                    )}
                  >
                    <span
                      className="h-3 w-3 flex-shrink-0 rounded-full"
                      style={{ background: t.swatch }}
                    />
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
